import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import type { NurseInShift } from "../../../../utils/models";
import { useUpdateLocationAndHoursForShift } from "../../../NormalizedNurseMenu/useUpdateLocationAndHours";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import type { GapsLocationEntry } from "../gapsDayModel";
import { GapsUnitList } from "./GapsUnitList";
import { GapsStaffStep } from "./gapsSheetTypes";
import { useGapsOtherUnits } from "./useGapsOtherUnits";

type Props = {
	nurseInShift: NurseInShift;
	name: string;
	/** The group's own units, with their counts for this window. */
	units: readonly GapsLocationEntry[];
	/** `""` when unassigned. */
	currentLocation: string;
	onStep: (step: GapsStaffStep) => void;
	onClose: () => void;
};

const SHEET = "gaps-change-location";

/**
 * Where this person works on this shift (P3-5): the window's own units first, emptiest first with their own
 * `filled/required`, then the department's other units — the list the desktop editor offers
 * (`getUniqueLocationNames`) — for a move the window's config does not anticipate.
 *
 * A location-only save: `updateLocationAndHours` directly, with no overtime cascade, since moving a person between
 * units changes neither their hours nor their pay. Tapping a unit IS the save, as picking an option is elsewhere.
 */
export const GapsStaffLocationStep: FC<Props> = ({ nurseInShift, name, units, currentLocation, onStep, onClose }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useHospitalData();
	const { updateLocationAndHours } = useUpdateLocationAndHoursForShift();
	const [savingLocation, setSavingLocation] = useState<string | null>(null);

	const otherUnits = useGapsOtherUnits(units);

	const handlePick = useCallback(
		async (locationName: string): Promise<void> => {
			// Picking where they already are is not a change; there is nothing to write.
			if (locationName === currentLocation) {
				onClose();
				return;
			}
			const properties = { department_id: chosenDepartment.department_id, shift_id: nurseInShift.id, from: currentLocation, to: locationName };
			track("gaps_staff_location_change_start", properties);
			setSavingLocation(locationName);
			const saved = await updateLocationAndHours({ originalNurseInShift: nurseInShift, updatedNurseInShift: { ...nurseInShift, location: locationName } });
			setSavingLocation(null);
			if (!saved) {
				// A changed location that did not save is a failed write — the mutation has shown its error snackbar.
				trackError("gaps_staff_location_change_error", properties);
				return;
			}
			track("gaps_staff_location_change_success", properties);
			showSnackbar(t("gaps.staffSheet.locationSaved", { name, unit: locationName }), "success");
			onClose();
		},
		[chosenDepartment.department_id, currentLocation, name, nurseInShift, onClose, showSnackbar, t, track, trackError, updateLocationAndHours],
	);

	return (
		<SheetStep
			stepKey={GapsStaffStep.Location}
			dataTestId={SHEET}
			title={t("gaps.staffSheet.locationTitle")}
			subtitle={t("gaps.staffSheet.locationSubtitle", { name })}
			footer={
				<SheetStepButton tone="dismiss" onClick={() => onStep(GapsStaffStep.Actions)} dataTestId={`${SHEET}-cancel`}>
					{t("back")}
				</SheetStepButton>
			}
		>
			<GapsUnitList
				units={units}
				otherUnits={otherUnits}
				currentLocation={currentLocation}
				savingLocation={savingLocation}
				onPick={(locationName) => void handlePick(locationName)}
				rowTestIdPrefix={`${SHEET}-option`}
			/>
		</SheetStep>
	);
};
